import { db, Ring } from 'technical/db';

type RingReport = Omit<Ring, 'desynchronizedAt'>;

export const ringSyncService = {
  async download(rings: RingReport[]): Promise<void> {
    await db.rings.bulkPut(
      rings.map((ring) => ({
        ...ring,
        desynchronizedAt: null,
      })),
    );
  },

  async bulkCompliance(
    startRingNumber: number,
    endRingNumber: number,
  ): Promise<boolean> {
    const result = await db.transaction(
      'rw',
      [db.rings, db.defectReports, db.repairReports],
      async () => {
        // search all rings
        const rings = (
          await db.rings
            .where('ringNumber')
            .between(startRingNumber, endRingNumber, true, true)
            .and((ring) => ring.keyPosition !== null)
            .toArray()
        ).filter((ring) => !ring.compliantDate);
        if (rings.length === 0) {
          return false;
        }

        // search all defects associated to rings
        const ringIds = rings.map((ring) => ring.id);
        const defects = await db.defectReports
          .where('ringId')
          .anyOf(ringIds)
          .toArray();

        // search all repairs
        const defectIds = defects.map((d) => d.id);
        const repairs =
          defectIds.length > 0
            ? await db.repairReports
                .where('defectReportId')
                .anyOf(defectIds)
                .toArray()
            : [];

        // all defects repaired
        const repairedDefectIds = new Set(repairs.map((r) => r.defectReportId));

        // search all ring with a defect to repair and not repaired
        const ringWithOngoingDefectIds = new Set(
          defects
            .filter(
              (defect) => defect.toRepair && !repairedDefectIds.has(defect.id),
            )
            .map((defect) => defect.ringId),
        );

        const ringsToUpdateIds = rings
          .filter((ring) => !ringWithOngoingDefectIds.has(ring.id))
          .map((ring) => ring.id);

        if (ringsToUpdateIds.length <= 0) {
          return false;
        }

        const now = new Date().toISOString();
        await db.rings.where('id').anyOf(ringsToUpdateIds).modify({
          compliantDate: now,
          desynchronizedAt: now,
        });
        return true;
      },
    );

    return result;
  },

  async getOneById(id: string): Promise<Ring | null> {
    return (await db.rings.get(id)) ?? null;
  },

  async hasOngoingDefect(id: string): Promise<boolean> {
    const defectsToRepair = await db.defectReports
      .where('ringId')
      .equals(id)
      .filter((defect) => defect.toRepair)
      .toArray();
    if (defectsToRepair.length <= 0) {
      return false;
    }

    const validatedRepairs = await db.repairReports
      .where('defectReportId')
      .anyOf(defectsToRepair.map((defect) => defect.id))
      .filter((repair) => repair.submittedAt !== null)
      .toArray();

    const defectFixedIds = new Set(
      validatedRepairs.map((repair) => repair.defectReportId),
    );
    const hasOngoingDefect = defectsToRepair.some(
      (defect) => !defectFixedIds.has(defect.id),
    );

    return hasOngoingDefect;
  },

  async getUnsyncedBatch(limit: number): Promise<Ring[]> {
    return db.rings
      .filter((r) => r.desynchronizedAt !== null)
      .limit(limit)
      .toArray();
  },

  async markAsSynchronized(ids: string[]): Promise<void> {
    await db.rings.bulkUpdate(
      ids.map((id) => {
        return {
          key: id,
          changes: {
            desynchronizedAt: null,
          },
        };
      }),
    );
  },

  async reset(): Promise<void> {
    await db.rings.clear();
  },
};
